import { z } from 'zod';
import { feldSchluessel, feldwertNormalisieren, KI_FELDTYPEN } from './produktfelder';
import { ProduktEntwurfSchema } from './produktPflege';

/**
 * Produkteigenschaften (Produktfelder) aus den Daten der Händler zuordnen und per KI ergänzen
 * (lib_produktpflege.php, anr_produkt in lib_ki.php der PHP-Middleware). Generisch über alle
 * freigegebenen Felder eines Marktplatzes – nichts Branchenspezifisches.
 */

/* ------------------------------------------------------------ Normalisierung */

const UMLAUTE: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** Für Vergleiche: Groß-/Kleinschreibung, Umlaute, Akzente, Leerzeichen, Unterstriche und Bindestriche egal */
export function normalisieren(s: string): string {
  return s
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => UMLAUTE[c] ?? c)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s_\-./:]+/g, '')
    .trim();
}

/** Levenshtein-Abstand mit Abbruch über der Grenze */
function abstand(a: string, b: string, grenze: number): number {
  if (Math.abs(a.length - b.length) > grenze) return grenze + 1;
  let vorher = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const jetzt = [i];
    let min = i;
    for (let j = 1; j <= b.length; j++) {
      const w = Math.min(vorher[j]! + 1, jetzt[j - 1]! + 1, vorher[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      jetzt.push(w);
      if (w < min) min = w;
    }
    if (min > grenze) return grenze + 1;
    vorher = jetzt;
  }
  return vorher[b.length]!;
}

/** Fast gleich (Tippfehler, englische/deutsche Endung: „automatic“ ~ „automatik“) – erst ab 5 Zeichen */
export function aehnlich(a: string, b: string): boolean {
  const x = normalisieren(a);
  const y = normalisieren(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const n = Math.min(x.length, y.length);
  if (n < 5) return false;
  return abstand(x, y, n >= 9 ? 2 : 1) <= (n >= 9 ? 2 : 1);
}

/* ------------------------------------------------------------ Felder */

export interface ZuordnungFeld {
  namespace: string;
  schluessel: string;
  name: string;
  typ: string;
  auswahl: string[];
  quellen: string[];
}

/** Listen-Typen (Shopify list.*): Wert als JSON-Array */
export const istListenTyp = (typ: string) => typ.startsWith('list.');

/** Typen, die die KI füllen darf: einfache Typen, Listen und alle Felder mit fester Auswahl */
export const kiFuellbar = (f: { typ: string; auswahl: string[] }) =>
  KI_FELDTYPEN.includes(f.typ) || f.auswahl.length > 0 || f.typ === 'list.single_line_text_field';

/**
 * Quelle für ein Feld vorschlagen: „namespace.key“, „metafields.namespace.key“, Schlüssel, Name oder eines der
 * Synonyme in „quellen“ – in beliebiger Schreibweise. null = nichts gefunden.
 */
export function feldQuelleVorschlagen(f: ZuordnungFeld, quellen: string[]): string | null {
  return feldQuellenKandidaten(f, quellen)[0] ?? null;
}

/** Alle passenden Quellen in der Reihenfolge des Vorschlags */
export function feldQuellenKandidaten(f: ZuordnungFeld, quellen: string[]): string[] {
  const voll = feldSchluessel(f);
  const kandidaten = [voll, `metafields.${voll}`, ...f.quellen, f.schluessel, f.name].map(normalisieren).filter(Boolean);
  const jeNorm = new Map<string, string>();
  for (const q of quellen) {
    const n = normalisieren(q);
    if (n && !jeNorm.has(n)) jeNorm.set(n, q);
  }
  const aus: string[] = [];
  for (const k of kandidaten) {
    const t = jeNorm.get(k);
    if (t && !aus.includes(t)) aus.push(t);
  }
  return aus;
}

/** Wert auf einen erlaubten Auswahlwert abbilden: genau, normalisiert gleich oder fast gleich */
export function auswahlTreffer(auswahl: string[], roh: string): string | null {
  const w = roh.trim();
  if (!w) return null;
  const n = normalisieren(w);
  return auswahl.find((a) => a === w) ?? auswahl.find((a) => normalisieren(a) === n) ?? auswahl.find((a) => aehnlich(a, w)) ?? null;
}

/** Rohwerte einer Liste: JSON-Array, sonst durch Komma, Semikolon oder | getrennt */
export function listeLesen(roh: unknown): string[] {
  if (Array.isArray(roh)) return roh.map((x) => String(x).trim()).filter(Boolean);
  const s = String(roh ?? '').trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    try {
      const a = JSON.parse(s) as unknown;
      if (Array.isArray(a)) return a.map((x) => String(x).trim()).filter(Boolean);
    } catch {
      /* kein JSON */
    }
  }
  return s.split(/[;,|]/).map((x) => x.trim()).filter(Boolean);
}

/** Tabellen-Schlüssel der Wertumsetzung: normalisiert, damit „Stainless Steel“ und „stainless_steel“ gleich sind */
export const wertSchluessel = (roh: string) => normalisieren(roh);

/**
 * Rohwert eines Feldes umsetzen: zuerst die Werttabelle der Anbindung, dann die Typprüfung
 * (feldwertNormalisieren), bei Auswahlfeldern auch normalisiert und fast gleich. null = passt nicht.
 */
export function wertUmsetzen(f: { typ: string; auswahl: string[] }, roh: unknown, tabelle: Record<string, string> = {}): string | null {
  if (roh === null || roh === undefined) return null;
  const nachTabelle = (x: string) => tabelle[wertSchluessel(x)] ?? x;
  if (istListenTyp(f.typ)) {
    const werte = listeLesen(roh).map(nachTabelle).map((x) => (f.auswahl.length ? auswahlTreffer(f.auswahl, x) : x)).filter((x): x is string => !!x);
    return werte.length ? JSON.stringify([...new Set(werte)]) : null;
  }
  if (typeof roh === 'object') return null;
  const w = nachTabelle(String(roh).trim());
  const direkt = feldwertNormalisieren(f, w);
  if (direkt !== null) return direkt;
  return f.auswahl.length ? auswahlTreffer(f.auswahl, w) : null;
}

/* ------------------------------------------------------------ Zuordnung je Anbindung */

/** Quelle je Feld (namespace.key → Spalte/Feld der Quelle); null = ausdrücklich keine Quelle */
export type FeldQuellen = Record<string, string | null>;
/** Wertumsetzung je Feld: normalisierter Rohwert → Wert des Marktplatzes */
export type FeldWerte = Record<string, Record<string, string>>;

const FeldName = z.string().trim().min(3).max(130).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/);

export const FeldzuordnungSchema = z.object({
  quellen: z.record(FeldName, z.string().trim().max(200).nullable()).default({}),
  werte: z
    .record(FeldName, z.record(z.string().trim().min(1).max(200), z.string().trim().max(200)))
    .default({})
    .transform((w) => {
      const aus: FeldWerte = {};
      for (const [feld, t] of Object.entries(w)) {
        const neu: Record<string, string> = {};
        for (const [roh, ziel] of Object.entries(t).slice(0, 300)) if (ziel && wertSchluessel(roh)) neu[wertSchluessel(roh)] = ziel;
        if (Object.keys(neu).length) aus[feld] = neu;
      }
      return aus;
    }),
});
export type FeldzuordnungEingabe = z.input<typeof FeldzuordnungSchema>;

/** Wert aus den Rohdaten (Groß-/Kleinschreibung egal) */
export function rohwert(rohdaten: Record<string, unknown>, quelle: string): unknown {
  if (quelle in rohdaten) return rohdaten[quelle];
  const n = normalisieren(quelle);
  for (const [k, v] of Object.entries(rohdaten)) if (normalisieren(k) === n) return v;
  return undefined;
}

/**
 * Felder eines Artikels aus seinen Rohdaten: eingestellte Quelle bzw. automatischer Vorschlag,
 * danach Wertumsetzung. Felder ohne passenden Wert fehlen im Ergebnis.
 */
export function felderAusQuelle(
  rohdaten: Record<string, unknown>,
  felder: ZuordnungFeld[],
  quellen: FeldQuellen = {},
  werte: FeldWerte = {},
): Record<string, string> {
  const schluessel = Object.keys(rohdaten);
  const aus: Record<string, string> = {};
  for (const f of felder) {
    const voll = feldSchluessel(f);
    // eingestellte Quelle (null = keine) oder die erste automatische mit passendem Wert
    const kandidaten = voll in quellen ? (quellen[voll] ? [quellen[voll]!] : []) : feldQuellenKandidaten(f, schluessel);
    for (const q of kandidaten) {
      const w = wertUmsetzen(f, rohwert(rohdaten, q), werte[voll]);
      if (w !== null) {
        aus[voll] = w;
        break;
      }
    }
  }
  return aus;
}

export interface FeldQuelleInfo {
  schluessel: string;
  /** spalte = Tabellenspalte, feld = Feld des Feeds/Shops, metafeld = Shopify-Metafeld, option = Varianten-Option */
  art: 'spalte' | 'feld' | 'metafeld' | 'option';
  beispiel: string;
}

export interface AnbindungFeldZeile {
  voll: string;
  name: string;
  typ: string;
  auswahl: string[];
  pflicht: boolean;
  /** eingestellte oder vorgeschlagene Quelle */
  quelle: string | null;
  /** Quelle stammt aus dem automatischen Vorschlag */
  automatisch: boolean;
  /** eingestellte Wertumsetzung (normalisierter Rohwert → Wert) */
  werte: Record<string, string>;
  /** Rohwerte der Beispielartikel mit Ergebnis der Umsetzung (null = passt nicht) und Vorschlag */
  rohwerte: { roh: string; schluessel: string; ergebnis: string | null; vorschlag: string | null }[];
}

export interface AnbindungFelder {
  felder: AnbindungFeldZeile[];
  quellen: FeldQuelleInfo[];
  /** erste Artikel mit den Feldwerten nach der Zuordnung */
  beispiele: { sku: string; titel: string; felder: Record<string, string> }[];
  /** Beispiele stammen aus dem letzten Lauf (z. B. Shopify-App) statt aus der Quelle */
  ausLauf: boolean;
}

/* ------------------------------------------------------------ KI-Anreicherung (nur Marktplatz) */

export interface AnreicherungVorschlag {
  feld: string;
  name: string;
  wert: string;
  /** bisheriger Wert, leer = Feld war leer */
  alt: string;
  begruendung: string;
  /** 0–100 */
  sicherheit: number;
}

export interface AnreicherungErgebnis {
  vorschlaege: AnreicherungVorschlag[];
  /** Verbrauch dieser Anfrage, nur bei „KI von DXL“ */
  kosten: { tokens: number; cent: number } | null;
  /** Kategorie gesucht, aber keine passt – Vorschlag für eine neue */
  neueKategorie?: string | null;
}

export const AnreichernSchema = z.object({
  /** auch Felder mit Wert neu vorschlagen */
  auchVorhandene: z.boolean().default(false),
  /** nur diese Felder (namespace.key); leer = alle zulässigen */
  felder: z.array(z.string().max(130)).max(200).default([]),
  /** nur Pflichtfelder (und Stammdaten, die der Marktplatz verlangt) */
  nurPflicht: z.boolean().default(false),
  /** auch Titel, Beschreibung, Marke und Kategorie vorschlagen, wenn sie fehlen (anr_luecken) */
  stammdaten: z.boolean().default(false),
  /** ungespeicherte Werte aus dem Editor */
  entwurf: ProduktEntwurfSchema.default({}),
});
export type AnreichernEingabe = z.input<typeof AnreichernSchema>;

export const AnreicherungUebernehmenSchema = z.object({
  /** Produktfelder (namespace.key) und Stammdaten (titel, beschreibung, marke, kategorie) */
  werte: z.record(z.string().max(130), z.string().max(100_000)),
});

export const ANREICHERUNG_MODI = { vorschlag: 'Vorschläge zur Bestätigung', direkt: 'Direkt übernehmen' } as const;
export type AnreicherungModus = keyof typeof ANREICHERUNG_MODI;

export const AnreicherungLaufSchema = z.object({
  /** bestimmte Produkte; leer = alle mit fehlenden Pflichtfeldern */
  ids: z.array(z.string().uuid()).max(5000).default([]),
  modus: z.enum(['vorschlag', 'direkt']).default('vorschlag'),
  /** direkt übernehmen nur mit ausdrücklicher Bestätigung */
  bestaetigt: z.boolean().default(false),
});
export type AnreicherungLaufEingabe = z.input<typeof AnreicherungLaufSchema>;

export type AnreicherungLaufStatus = 'laeuft' | 'fertig' | 'abgebrochen' | 'fehler';

export interface AnreicherungLauf {
  id: string;
  status: AnreicherungLaufStatus;
  modus: AnreicherungModus;
  auswahl: number | null;
  bearbeitet: number;
  vorschlaege: number;
  uebernommen: number;
  fehler: number;
  tokens: number;
  cent: number;
  meldung: string;
  gestartetAm: string;
  beendetAm: string | null;
}

export interface AnreicherungSchaetzung {
  produkte: number;
  /** mehr Produkte als gezählt */
  mehr: boolean;
  tokens: number;
  cent: number;
  /** null = eigener Schlüssel (keine Abrechnung durch DXL) */
  dxl: boolean;
  tageslimitCent: number | null;
  heuteCent: number | null;
}

export interface AnreicherungVorschlagZeile extends AnreicherungVorschlag {
  id: string;
  produktId: string;
  produktTitel: string;
  laufId: string | null;
  erstelltAm: string;
}

export interface AnreicherungVorschlagSeite {
  eintraege: AnreicherungVorschlagZeile[];
  weiter: string | null;
}

export const VorschlaegeEntscheidenSchema = z.object({
  annehmen: z.array(z.string().uuid()).max(500).default([]),
  ablehnen: z.array(z.string().uuid()).max(500).default([]),
  /** alle offenen Vorschläge eines Laufs */
  laufId: z.string().uuid().optional(),
  alle: z.enum(['annehmen', 'ablehnen']).optional(),
});
export type VorschlaegeEntscheidenEingabe = z.input<typeof VorschlaegeEntscheidenSchema>;

export interface AnreicherungUebersicht {
  ki: { eingerichtet: boolean; quelle: KiQuelle | null; bilder: boolean };
  laeufe: AnreicherungLauf[];
  offen: number;
}

/* ------------------------------------------------------------ KI von DXL: Verbrauch und Preise */

export const KI_QUELLEN = { eigen: 'Eigener Schlüssel', dxl: 'KI von DXL' } as const;
export type KiQuelle = keyof typeof KI_QUELLEN;

/** Aufschlag auf den Einkaufspreis des Anbieters */
export const KI_AUFSCHLAG_PROZENT = 10;
/** Preis für den Marktplatz aus dem Einkaufspreis (beides Mikro-Cent) */
export const kiPreisAus = (einkaufMikrocent: number) => Math.round((einkaufMikrocent * (100 + KI_AUFSCHLAG_PROZENT)) / 100);
/** Mikro-Cent (1/1.000.000 Cent) → Cent, kaufmännisch gerundet */
export const mikrocentZuCent = (m: number) => Math.round(m / 1_000_000);

/** Einkaufspreise je Modell in Cent je 1 Mio. Tokens (= Mikro-Cent je Token) */
export const KiPreisSchema = z.object({
  anbieter: z.string().trim().max(40).default(''),
  modell: z.string().trim().min(1, 'Bitte die Modell-ID angeben').max(120),
  eingabeCentJeMio: z.coerce.number().min(0).max(10_000_000),
  ausgabeCentJeMio: z.coerce.number().min(0).max(10_000_000),
});
export const KiPreiseSchema = z.object({ preise: z.array(KiPreisSchema).max(200) });
export type KiPreisEingabe = z.input<typeof KiPreisSchema>;
export interface KiPreis {
  anbieter: string;
  modell: string;
  eingabeCentJeMio: number;
  ausgabeCentJeMio: number;
  geaendertAm: string;
}

export interface KiVerbrauchStand {
  tokens: number;
  cent: number;
}

/** Einstellungen → KI: Wahl der Quelle und Stand „KI von DXL“ */
export interface KiDxlStand {
  quelle: KiQuelle;
  /** Plattform-KI eingerichtet und Preis hinterlegt */
  verfuegbar: boolean;
  /** von DXL für diesen Marktplatz gesperrt */
  gesperrt: boolean;
  /** Add-on „KI von DXL“ frei (im Paket, gebucht, von DXL freigeschaltet oder Bestandsschutz) */
  addon: boolean;
  anbieter: string;
  modell: string;
  /** Preise für den Marktplatz (inkl. Aufschlag) in Cent je 1 Mio. Tokens */
  preis: { eingabeCentJeMio: number; ausgabeCentJeMio: number } | null;
  tageslimitCent: number;
  heute: KiVerbrauchStand;
  monat: KiVerbrauchStand;
  bestaetigtAm: string | null;
  /** Guthaben für die KI von DXL (Vorkasse), null = nicht nötig */
  guthaben: { verfuegbarCent: number; reserviertCent: number; niedrig: boolean } | null;
}

export const KiQuelleSchema = z.object({
  quelle: z.enum(['eigen', 'dxl']),
  /** Kosten zur Kenntnis genommen (Pflicht für „KI von DXL“) */
  bestaetigt: z.boolean().default(false),
  /** Kostenbremse je Tag in Cent; 0 = keine Anfragen */
  tageslimitCent: z.coerce.number().int().min(0).max(1_000_000).optional(),
});
export type KiQuelleEingabe = z.input<typeof KiQuelleSchema>;

/** Plattform-Admin: KI-Zugang von DXL */
export interface PlattformKi {
  anbieter: string;
  modell: string;
  modellWirksam: string;
  schluesselGesetzt: boolean;
  geprueftAm: string | null;
  geaendertAm: string | null;
}

export const PlattformKiSchema = z.object({
  anbieter: z.union([z.enum(['anthropic', 'openai', 'gemini']), z.literal('')]),
  modell: z.string().trim().max(120).regex(/^[A-Za-z0-9._:\/@-]*$/).default(''),
  schluessel: z.string().max(500).optional(),
  schluesselLoeschen: z.boolean().optional(),
});
export type PlattformKiEingabe = z.input<typeof PlattformKiSchema>;

export interface KiVerbrauchMandant {
  mandantId: string;
  kuerzel: string;
  name: string;
  quelle: KiQuelle;
  gesperrt: boolean;
  monat: KiVerbrauchStand & { einkaufCent: number };
}

export const KiSperreSchema = z.object({ gesperrt: z.boolean() });

/** Kann der Anbieter Bilder per Adresse lesen? (Gemini nur über hochgeladene Dateien – dort ohne Bilder) */
export const kiKannBilder = (anbieter: string) => anbieter === 'anthropic' || anbieter === 'openai';
